import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { SourceTypes } from "@common/types/server-list-types";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { updateSourceName } from "@src/cloud-connect/use-cases/update-source-name";
import { useI18N } from "@src/language/language-context";

import { RootState } from "@src/reducers/store";
import {
  updateConnectedGatewaysListData,
  updateConnectedServerListData,
} from "@src/reducers/your-reducer";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export interface UseEditSourceNameGatewaysListHookProps {
  showInput: boolean;
  editSourceName: (e) => void;
  onKeyDownFunc: (e) => void;
  updateNewSourceName: (e, value: string) => void;
  saveSourceName: (e) => void;
  cancelSaveSourceName: (e) => void;
  newSourceName: string;
}

export const updateSourceNameInLists = (
  clonedGatewaysList: GatewayWithSourceViewStateTypes[],
  gatewayUuid: string,
  clonedSourcesListData: SourceTypes[],
  sourceId: string,
  newSourceName: string,
): { gatewaysUpdated: boolean; sourcesUpdated: boolean } => {
  let gatewaysUpdated = false;
  let sourcesUpdated = false;

  const foundClonedGateway = clonedGatewaysList.find(
    (clonedGateway) => clonedGateway.gateway_uuid === gatewayUuid,
  );

  if (foundClonedGateway) {
    const foundTargetedSource = foundClonedGateway.sources.find(
      (source) => source.source_id === sourceId,
    );

    if (foundTargetedSource && foundTargetedSource.name !== newSourceName) {
      foundTargetedSource.name = newSourceName;
      gatewaysUpdated = true;
    }
  }

  const clonedSource = clonedSourcesListData.find(
    (source) => source.source_id === sourceId,
  );

  if (clonedSource && clonedSource.name !== newSourceName) {
    clonedSource.name = newSourceName;
    sourcesUpdated = true;
  }

  return { gatewaysUpdated, sourcesUpdated };
};

export const useEditSourceNameGatewaysListHook = (
  gateway: GatewayWithSourceViewStateTypes,
  source: SourceTypes,
): UseEditSourceNameGatewaysListHookProps => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const previousName = source.name;

  const connectedSourcesListData = useSelector(
    (state: RootState) => state.yourSlice.connectedServerListData,
  );

  const connectedGatewaysListData = useSelector(
    (state: RootState) => state.yourSlice.connectedGatewaysListData,
  );

  const logger = getLogger();
  const [showInput, setShowInput] = useState(false);
  const [newSourceName, setNewSourceName] = useState(source.name);

  const texts = {
    errorInputEmpty: i18n("modules.editSourceName.errorInputEmpty"),
    errorUpdateFailed: i18n(
      "modules.editSourceName.errorUpdateFailed",
    ),
    successfullyUpdated: i18n(
      "modules.editSourceName.successfullyUpdated",
    ),
  };

  const updateNewSourceName = (e, value: string) => {
    e.stopPropagation();
    logger.debug("Updating name:", value);
    setNewSourceName(value);
  };

  const editSourceName = (e) => {
    e.stopPropagation();
    logger.debug("editSourceName");
    setShowInput(true);
  };

  const onKeyDownFunc = (e) => {
    e.stopPropagation();
    logger.debug("onKeyDownFunc");
    if (e.key === "Enter") {
      e.preventDefault();
      saveSourceName(e);
    }
  };

  const updateItemNameInReduxState = (newSourceNameToBeSaved: string) => {
    logger.debug("updateItemNameInReduxState", newSourceNameToBeSaved);

    const clonedGatewaysList = structuredClone(connectedGatewaysListData);
    const clonedServerListData = structuredClone(connectedSourcesListData);

    const { gatewaysUpdated, sourcesUpdated } = updateSourceNameInLists(
      clonedGatewaysList,
      gateway.gateway_uuid,
      clonedServerListData,
      source.source_id,
      newSourceNameToBeSaved,
    );

    if (gatewaysUpdated) {
      dispatch(updateConnectedGatewaysListData(clonedGatewaysList));
    }
    if (sourcesUpdated) {
      dispatch(updateConnectedServerListData(clonedServerListData));
    }
  };

  const updateSourceNameInApi = async (newSourceNameToBeSaved: string) => {
    logger.debug("updateSourceNameInApi", newSourceNameToBeSaved);

    const response = await updateSourceName({
      name: newSourceNameToBeSaved,
      source_id: source.source_id,
      source_type: source.source_type,
    });

    return response;
  };

  const saveSourceName = async (e) => {
    e.stopPropagation();
    logger.debug("saveSourceName", newSourceName);
    const newSourceNameToBeSaved = newSourceName.trim();
    setNewSourceName(newSourceNameToBeSaved);

    if (!newSourceName) {
      errorSnackbar(texts.errorInputEmpty);
      return;
    }

    setShowInput(false);

    const response = await updateSourceNameInApi(newSourceNameToBeSaved);
    if (response && response.success) {
      successSnackbar(texts.successfullyUpdated, 5000);
      updateItemNameInReduxState(newSourceNameToBeSaved);
    } else {
      errorSnackbar(texts.errorUpdateFailed, 5000);
      setNewSourceName(previousName);
    }
  };

  const cancelSaveSourceName = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    logger.debug("CancelSaveSourceName", newSourceName);
    setNewSourceName(source.name);
    setShowInput(false);
  };

  useEffect(() => {
    logger.debug("EditSourceName mounted");
    logger.debug("EditSourceName item", source);

    setNewSourceName(source.name);
    return () => {
      logger.debug("EditSourceName unmounted");
    };
  }, []);

  return {
    showInput: showInput,
    editSourceName: editSourceName,
    onKeyDownFunc: onKeyDownFunc,
    updateNewSourceName: updateNewSourceName,
    saveSourceName: saveSourceName,
    cancelSaveSourceName: cancelSaveSourceName,
    newSourceName: newSourceName,
  };
};
